import { createReducer, isAnyOf } from "@reduxjs/toolkit";
import { deleteItemFromArray } from "@helpers/common";

import {
  createPost,
  createPostSuccess,
  createPostFailure,
  getPosts,
  getPostsSuccess,
  getPostsFailure,
  getPortion,
  getPortionSuccess,
  getPortionFailure,
  updatePost,
  postUploaded,
  deletePost,
  deletePostSuccess,
  deletePostFailure,
  createPostReset,
  likePostSuccess,
  unlikePostSuccess,
  showReportedPostById,
  getFeedPostsSuccess,
  getFeedPosts,
  getFeedPortion,
  getFeedPortionSuccess,
  getFeedPortionFailure,
  getFeedPostsFailure,
  setPosts,
  editPost,
  editPostSuccess,
  editPostFailure,
  editPostReset,
  pinPost,
  pinPostFailure,
  pinPostSuccess,
  unpinPost,
  unpinPostFailure,
  unpinPostSuccess,
  updatePurchasedPost,
  getFeedPurchasedPosts,
  getFeedPurchasedPostsSuccess,
  getFeedPurchasedPostsFailure,
  getFeedPurchasedPortion,
  getFeedPurchasedPortionSuccess,
  getFeedPurchasedPortionFailure,
  toggleShowNewPostsButton,
  showNewPostsButton,
  toggleNewPostsLoading,
  clearPosts,
} from "./actions";
import { followCreatorSuccess } from "../creatorProfile/actions";

// todo: handle all user posts for feed

/**
 * @property {number[]} allPosts array of post ids
 * @property {object[]} postsById { [postId]: {postContent} }
 */
const initialState = {
  allPosts: [],
  purchasedPosts: [],
  postById: {},
  totalCount: undefined,
  purchasedTotalCount: undefined,
  isLoading: false,
  isCreated: false,
  isCreating: false,
  error: null,
  deletePostError: null,
  isDeleting: false,
  isEditing: false,
  isEdited: false,
  isPinning: false,
  isLoadingNext: false,
  isLoadingFirstTime: false,
  isAllLoading: false,
  isAllLoadingFirstTime: false,
  isPurchasedLoading: false,
  isPurchasedLoadingFirstTime: false,
  isAllLoadingNext: false,
  isPurchasedLoadingNext: false,
  newPostsLoading: false,
  showNewPostsButtonVisible: false,
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(createPost.type, (state) => ({
      ...state,
      isCreated: false,
      isCreating: true,
    }))
    .addCase(createPostSuccess.type, (state) => ({
      ...state,
      isCreated: true,
      isCreating: false,
    }))
    .addCase(createPostFailure.type, (state, action) => ({
      ...state,
      isLoading: false,
      isCreating: false,
      error: action.payload,
    }))
    .addCase(createPostReset.type, (state) => ({
      ...state,
      isCreated: false,
      isCreating: false,
    }))

    .addCase(getPosts.type, (state) => ({
      ...state,
      isLoading: true,
      isLoadingFirstTime: true,
    }))
    .addCase(getPostsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      error: initialState.error,
      isLoading: false,
      isLoadingFirstTime: false,
    }))
    .addCase(getPostsFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isLoading: false,
      isLoadingFirstTime: false,
    }))
    .addCase(getPortion.type, (state) => ({
      ...state,
      isLoading: true,
      isLoadingNext: true,
    }))
    .addCase(getPortionSuccess.type, (state, { payload }) => {
      const { allPosts, postById } = state;
      const {
        allPosts: newPosts,
        postById: newPostsById,
        totalCount,
      } = payload;

      return {
        ...state,
        allPosts: [...allPosts, ...newPosts],
        postById: { ...postById, ...newPostsById },
        totalCount,
        error: initialState.error,
        isLoading: false,
        isLoadingNext: false,
      };
    })
    .addCase(getPortionFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isLoadingNext: false,
      isLoading: false,
    }))
    .addCase(postUploaded.type, (state, { payload }) => {
      const { allPosts, postById } = state;

      const isPinned = allPosts[0] ? postById[allPosts[0]]?.isPinned : false;

      const updatedAllPosts = [...allPosts];

      if (isPinned) {
        updatedAllPosts.splice(1, 0, payload.id);
      } else {
        updatedAllPosts.splice(0, 0, payload.id);
      }

      return {
        ...state,
        postById: { ...state.postById, [payload.id]: payload },
        allPosts: updatedAllPosts,
      };
    })
    .addCase(updatePost.type, (state, { payload }) => ({
      ...state,
      postById: {
        ...state.postById,
        [payload.id]: { ...state.postById?.[payload.id], ...payload.post },
      },
    }))
    .addCase(deletePost.type, (state) => ({
      ...state,
      isDeleting: true,
    }))
    .addCase(deletePostSuccess.type, (state, { payload }) => ({
      ...state,
      isDeleting: false,
      deletePostError: initialState.deletePostError,
      allPosts: deleteItemFromArray(state.allPosts, payload.id),
    }))
    .addCase(deletePostFailure.type, (state, { payload }) => ({
      ...state,
      isDeleting: false,
      deletePostError: payload,
    }))
    .addCase(pinPostSuccess.type, (state, { payload: { id } }) => {
      const { allPosts, postById } = state;

      const updatedAllPosts = [...allPosts];

      const postIndex = allPosts.findIndex((postId) => postId === id);
      updatedAllPosts.splice(0, 0, updatedAllPosts.splice(postIndex, 1)[0]);

      return {
        ...state,
        allPosts: [...updatedAllPosts],
        postById: { ...postById, [id]: { ...postById[id], isPinned: true } },
        isPinning: false,
      };
    })
    .addCase(unpinPostSuccess.type, (state, { payload: { id, position } }) => {
      const { allPosts, postById } = state;

      const updatedAllPosts = [...allPosts];

      //remove first pinned postId
      updatedAllPosts.shift();

      if (position < allPosts.length) {
        updatedAllPosts.splice(position, 0, id);
      }

      return {
        ...state,
        allPosts: [...updatedAllPosts],
        postById: { ...postById, [id]: { ...postById[id], isPinned: false } },
        isPinning: false,
      };
    })
    .addCase(showReportedPostById.type, (state, { payload: { postById } }) => {
      return {
        ...state,
        postById: {
          ...state.postById,
          [postById]: {
            ...state.postById?.[postById],
            showBlurredPostAnywhere: true,
          },
        },
      };
    })
    .addCase(getFeedPosts.type, (state) => ({
      ...state,
      isAllLoading: true,
      isAllLoadingFirstTime: true,
    }))
    .addCase(getFeedPostsSuccess.type, (state, action) => {
      const { postById } = state;
      const { postById: newPostsById } = action.payload;

      return {
        ...state,
        ...action.payload,
        error: initialState.error,
        postById: { ...postById, ...newPostsById },
        isAllLoading: false,
        isAllLoadingFirstTime: false,
        newPostsLoading: false,
        showNewPostsButtonVisible: false,
      };
    })
    .addCase(getFeedPostsFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isAllLoading: false,
      newPostsLoading: false,
      showNewPostsButtonVisible: false,
      isAllLoadingFirstTime: false,
    }))
    .addCase(getFeedPurchasedPosts.type, (state) => ({
      ...state,
      isPurchasedLoading: false,
      isPurchasedLoadingFirstTime: false,
      isLoading: false,
    }))
    .addCase(getFeedPurchasedPostsSuccess.type, (state, action) => {
      const { postById } = state;
      const { postById: newPostsById } = action.payload;

      return {
        ...state,
        ...action.payload,
        error: initialState.error,
        postById: { ...postById, ...newPostsById },
        isPurchasedLoading: false,
        isPurchasedLoadingFirstTime: false,
        isLoading: false,
      };
    })
    .addCase(getFeedPurchasedPostsFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isPurchasedLoading: false,
      isPurchasedLoadingFirstTime: false,
      isLoading: false,
    }))
    .addCase(getFeedPortion.type, (state) => ({
      ...state,
      isAllLoading: true,
      isAllLoadingNext: true,
    }))
    .addCase(getFeedPortionSuccess.type, (state, { payload }) => {
      const { allPosts, postById } = state;
      const {
        allPosts: newPosts,
        postById: newPostsById,
        totalCount,
      } = payload;

      return {
        ...state,
        allPosts: [...allPosts, ...newPosts],
        postById: { ...postById, ...newPostsById },
        totalCount,
        error: initialState.error,
        isAllLoading: false,
        isAllLoadingNext: false,
      };
    })
    .addCase(getFeedPortionFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isAllLoading: false,
      isAllLoadingNext: false,
    }))
    .addCase(getFeedPurchasedPortion.type, (state) => ({
      ...state,
      isPurchasedLoading: true,
      isPurchasedLoadingNext: true,
    }))
    .addCase(getFeedPurchasedPortionSuccess.type, (state, { payload }) => {
      const { purchasedPosts, postById } = state;
      const {
        purchasedPosts: newPosts,
        postById: newPostsById,
        purchasedTotalCount,
      } = payload;

      return {
        ...state,
        purchasedPosts: [...purchasedPosts, ...newPosts],
        postById: { ...postById, ...newPostsById },
        purchasedTotalCount,
        error: initialState.error,
        isPurchasedLoading: false,
        isPurchasedLoadingNext: false,
      };
    })
    .addCase(getFeedPurchasedPortionFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isPurchasedLoading: false,
      isPurchasedLoadingNext: false,
    }))
    .addCase(setPosts.type, (state, { payload }) => {
      const { postById } = state;
      const { postById: newPostById } = payload;

      return {
        ...state,
        postById: { ...postById, ...newPostById },
        error: initialState.error,
        isLoading: false,
      };
    })
    .addCase(editPost, (state) => {
      state.isEditing = true;
    })
    .addCase(editPostSuccess, (state, { payload }) => {
      const { result, post } = payload;

      state.isEditing = false;
      state.isEdited = true;
      if (state.postById) {
        state.postById[result] = post;
      }
    })
    .addCase(editPostFailure, (state) => {
      state.isEditing = false;
      state.isEdited = false;
    })
    .addCase(editPostReset, (state) => {
      state.isEditing = initialState.isEditing;
      state.isEdited = initialState.isEdited;
    })
    .addCase(updatePurchasedPost, (state, { payload }) => {
      return {
        ...state,
        postById: {
          ...state.postById,
          [payload.post.id]: {
            ...state.postById?.[payload.post.id],
            content: payload.post.content,
            previewImage: payload.post.previewImage,
            isLocked: false,
            title: payload.post.title,
            description: payload.post.description,
          },
        },
        purchasedPosts: [payload.post.id, ...state.purchasedPosts],
      };
    })
    .addCase(followCreatorSuccess.type, (state, { payload }) => {
      if (state?.postById?.[payload.id]) {
        state.postById = {
          ...state.postById,
          [payload.postId]: {
            ...state.postById[payload.postId],
            isLocked: false,
          },
        };
      }

      return state;
    })
    .addCase(toggleShowNewPostsButton.type, (state, { payload }) => {
      return {
        ...state,
        showNewPostsButtonVisible: payload,
      };
    })
    .addCase(showNewPostsButton.type, (state) => {
      return {
        ...state,
        showNewPostsButtonVisible: true,
      };
    })
    .addCase(toggleNewPostsLoading.type, (state, { payload }) => {
      return {
        ...state,
        newPostsLoading: payload,
      };
    })
    .addCase(clearPosts.type, () => {
      return {
        ...initialState,
      };
    })
    .addMatcher(
      isAnyOf(likePostSuccess, unlikePostSuccess),
      (state, { payload }) => {
        const { postId, likesCount } = payload;

        return {
          ...state,
          postById: {
            ...state.postById,
            [postId]: {
              ...state.postById?.[postId],
              likesCount: likesCount,
              isLiked: !state?.postById?.[postId].isLiked,
            },
          },
        };
      },
    )
    .addMatcher(isAnyOf(pinPost.type, unpinPost.type), (state) => ({
      ...state,
      isPinning: true,
    }))
    .addMatcher(
      isAnyOf(pinPostFailure.type, unpinPostFailure.type),
      (state) => ({
        ...state,
        isPinning: false,
      }),
    );
});
